<script setup lang="ts">
/**
 * CriticalFailureBanner —— Aggregate 全失败时的整页显式降级（F07 + F08）。
 *
 * 触发条件：Showcase Store `state.status === 'failed'`（Aggregate
 * Endpoint 完全拿不到数据，首屏无任何内容）。UI = 整页 Banner +
 * Copy Catalog 文案 + 重试按钮（点击调 `store.retry()`）。
 *
 * **F08 预览过期分支**：`state.isPreview` 且 errorCode ∈
 * {`auth.preview_token_expired`, `auth.preview_token_invalid`} → 改显
 * `copy.preview.tokenExpired` 专属文案 + **隐藏重试按钮**（重试无意义，
 * token 已过期/失效，需回管理端重新生成）。其余失败态照常显重试按钮。
 *
 * **Debug-First（AGENTS §六）**：挂载时 `console.error` 完整失败原因
 * （errorCode + traceId + status + message），不静默抹错；测试断言
 * 该 console.error 被调用且含 errorCode。Banner 是用户可见的显式
 * 提示，不是静默 fallback —— 错误信号全保留。
 *
 * 文案走 Copy Catalog `copy.fallback.critical` + `copy.buttons.retry`，
 * 业务组件不写死字符串（F07 acceptance）。
 */
import { computed, onMounted } from 'vue';

import Icon from '@/components/Icon.vue';
import { useShowcase } from '@/store/use-showcase';
import type { ShowcaseError } from '@/store/showcase';
import { copy } from '@/i18n/zh-CN';

/** Banner 头部错误图标尺寸（px）。 */
const ICON_SIZE_HERO = 40;
/** 重试按钮内图标尺寸（px）。 */
const ICON_SIZE_BTN = 16;
/** 预览 token 失效类 errorCode 集合（后端 AuthErrorCodes，B14）。 */
const PREVIEW_TOKEN_ERROR_CODES = new Set([
  'auth.preview_token_expired',
  'auth.preview_token_invalid',
]);

const props = defineProps<{
  /** store 失败态错误对象；契约上 failed 必带 error，null 视为非法态并显式暴露。 */
  error: ShowcaseError | null;
}>();

const store = useShowcase();

/** 预览态 + token 过期/失效 → 隐藏重试按钮，改显专属文案。 */
const isPreviewTokenExpired = computed(
  () =>
    store.state.isPreview
    && props.error !== null
    && PREVIEW_TOKEN_ERROR_CODES.has(props.error.errorCode),
);

const title = computed(() =>
  isPreviewTokenExpired.value
    ? copy.preview.tokenExpired
    : copy.fallback.critical.title,
);
const description = computed(() =>
  isPreviewTokenExpired.value
    ? copy.preview.tokenExpired
    : copy.fallback.critical.description,
);

onMounted(() => {
  if (props.error === null) {
    // Debug-First：failed 态缺 error 是 store 契约违规，显式暴露不静默白屏。
    // eslint-disable-next-line no-console
    console.error('[CriticalFailure] failed status but error is null (store contract violation)');
    return;
  }
  // eslint-disable-next-line no-console
  console.error(
    '[CriticalFailure]',
    `errorCode=${props.error.errorCode}`,
    `traceId=${props.error.traceId}`,
    `status=${props.error.status}`,
    props.error.message,
  );
});

function onRetry(): void {
  void store.retry();
}
</script>

<template>
  <div
    :class="$style.banner"
    data-testid="critical-failure"
    role="alert"
  >
    <Icon :class="$style.icon" name="error" :size="ICON_SIZE_HERO" />
    <h2 :class="$style.title">{{ title }}</h2>
    <p :class="$style.description">{{ description }}</p>
    <p v-if="props.error" :class="$style.meta">
      <code>{{ props.error.errorCode }}</code>
    </p>
    <button
      v-if="!isPreviewTokenExpired"
      :class="$style.retry"
      data-testid="critical-retry"
      type="button"
      :aria-label="copy.fallback.critical.retryAria"
      @click="onRetry"
    >
      <Icon :class="$style.retryIcon" name="retry" :size="ICON_SIZE_BTN" />
      {{ copy.buttons.retry }}
    </button>
  </div>
</template>

<style module>
.banner {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--space-5) var(--space-3);
  border: 1px solid var(--color-brand-primary);
  border-radius: var(--radius-md);
  background: var(--color-bg-primary);
  color: var(--color-brand-primary);
  text-align: center;
}

.icon {
  color: var(--color-brand-primary);
  margin: 0 auto var(--space-2);
}

.title {
  margin: 0 0 var(--space-1);
  color: var(--color-brand-primary);
  font-size: var(--font-size-lg);
}

.description {
  margin: 0 0 var(--space-2);
  color: var(--color-text-secondary);
  font-size: var(--font-size-md);
}

.meta {
  margin: 0 0 var(--space-3);
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
}

.meta code {
  background: var(--color-bg-secondary);
  padding: 0 var(--space-1);
  border-radius: var(--radius-sm);
}

.retry {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  background: var(--color-brand-primary);
  color: var(--color-bg-primary);
  border: none;
  border-radius: var(--radius-sm);
  padding: var(--space-1) var(--space-3);
  font-size: var(--font-size-base);
  transition: background var(--template-hover-duration) ease;
}

.retry:hover {
  background: var(--color-brand-accent);
}

.retryIcon {
  color: var(--color-bg-primary);
}
</style>
